import { TablePaginationConfig } from 'antd'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useMediaQuery } from 'react-responsive'
import { ActionEnum, getExpectNumber } from '../../../common'
import {
  ColumnActionHeader,
  ColumnInputHeader,
  ColumnInputRangeHeader,
  NumberFormat,
  PageTitleComponent,
  TableActionColumnButtonComponent,
  TableComponent,
} from '../../../components'
import { DebtEntity } from '../../../models'
import { RootState } from '../../../stores'
import {
  loadDebts,
  setAction,
  setDebtFilter,
  setShowSearchDrawer,
} from '../../../stores/actions'
import {
  ManageDebtModalComponent,
  SearchDebtDrawerComponent,
} from './components'

export const DebtPage: React.FC = () => {
  const dispatch = useDispatch()
  const { management } = useSelector((state: RootState) => state.common)
  const { isDebtsLoaded, debtFilter, debts, totalDebts } = useSelector(
    (state: RootState) => state.debt
  )
  const isDesktopScreen = useMediaQuery({ minWidth: '1300px' })
  const [selectedCardId, setSelectedCardId] = React.useState<string>()
  const [selectedStationId, setSelectedStationId] = React.useState<number>()
  const [filter, setFilter] = React.useState(debtFilter)

  React.useEffect(() => {
    if (isDesktopScreen) {
      dispatch(setShowSearchDrawer(false))
    }
    // eslint-disable-next-line
  }, [isDesktopScreen])

  React.useEffect(() => {
    dispatch(loadDebts())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  React.useEffect(() => {
    setFilter(debtFilter)
    dispatch(loadDebts())
  }, [debtFilter, dispatch])

  const columns = React.useMemo(
    () => [
      {
        title: (
          <ColumnInputHeader
            titleKey="debt.card.id"
            value={filter.cardId}
            onChange={(e) => setFilter({ ...filter, cardId: e.target.value })}
          />
        ),
        width: 130,
        render: (_: any, debt: DebtEntity) => debt.card.id,
      },
      {
        title: (
          <ColumnInputHeader
            titleKey="debt.customer"
            value={filter.customerName}
            onChange={(e) =>
              setFilter({ ...filter, customerName: e.target.value })
            }
          />
        ),
        width: 120,
        render: (_: any, debt: DebtEntity) => debt.card.customer?.name,
      },
      {
        title: (
          <ColumnInputRangeHeader
            titleKey="debt.accountsPayable"
            fromValue={filter.totalAccountsPayableFrom}
            toValue={filter.totalAccountsPayableTo}
            onToChange={(e: any) =>
              setFilter({
                ...filter,
                totalAccountsPayableTo: e.target.value,
              })
            }
            onFromChange={(e: any) =>
              setFilter({
                ...filter,
                totalAccountsPayableFrom: e.target.value,
              })
            }
            fromKey="common.from"
            toKey="common.to"
          />
        ),
        width: 200,
        render: (_: any, debt: DebtEntity) => (
          <NumberFormat
            value={debt.totalAccountsPayable}
            suffixKey="common.moneyType"
          />
        ),
      },
      {
        title: (
          <ColumnInputHeader
            titleKey="debt.station"
            value={filter.stationName}
            onChange={(e) =>
              setFilter({ ...filter, stationName: e.target.value })
            }
          />
        ),
        width: 130,
        render: (_: any, debt: DebtEntity) =>
          `${debt.station.name} (${debt.station.address})`,
      },
      {
        title: (
          <ColumnActionHeader
            onSearch={() =>
              dispatch(
                setDebtFilter({
                  ...filter,
                  pageIndex: 1,
                  totalAccountsPayableFrom: getExpectNumber(
                    true,
                    filter.totalAccountsPayableFrom,
                    filter.totalAccountsPayableTo
                  ),
                  totalAccountsPayableTo: getExpectNumber(
                    false,
                    filter.totalAccountsPayableFrom,
                    filter.totalAccountsPayableTo
                  ),
                })
              )
            }
          />
        ),
        render: (_: any, debt: DebtEntity) => (
          <TableActionColumnButtonComponent
            onViewButtonClick={() => {
              setSelectedCardId(debt.card.id)
              setSelectedStationId(debt.station.id)
              dispatch(setAction(ActionEnum.VIEW))
            }}
          />
        ),
        width: 150,
      },
    ],
    [dispatch, filter]
  )

  return (
    <div style={{ height: '97.5%' }}>
      {management.showManageModal && (
        <ManageDebtModalComponent
          cardId={selectedCardId}
          stationId={selectedStationId}
        />
      )}
      <PageTitleComponent hasSearchDrawer />
      {management.showSearchDrawer && <SearchDebtDrawerComponent />}
      <TableComponent
        data={debts}
        columns={columns}
        loading={!isDebtsLoaded}
        paginationConfig={{
          total: totalDebts,
          current: debtFilter.pageIndex,
        }}
        onChange={(pagination: TablePaginationConfig) => {
          const pageIndex = pagination.current || filter.pageIndex
          setFilter({ ...filter, pageIndex })
          dispatch(setDebtFilter({ ...filter, pageIndex }))
        }}
      />
    </div>
  )
}
